// Pure date maths for the dashboard attendance widget. No date library: the whole
// job is Monday-first week bounds, month bounds and a few labels, and every one of
// these is unit-tested (attendance-calendar.test.ts).
//
// Dates are handled as LOCAL calendar days, never UTC instants -- an attendance
// calendar is about the day a person was at work, so parsing "2026-10-06" as UTC
// midnight would shift it a day for anyone west of Greenwich.

export type DayStatus = "present" | "pending" | "absent" | "leave" | "holiday" | "weekly_off" | "future" | "none";

export interface AttendanceDay {
  /** "YYYY-MM-DD" */
  date: string;
  status: DayStatus;
  /** Leave type or holiday name, when one applies. */
  note?: string;
}

export type PeriodMode = "week" | "month";

/** "YYYY-MM-DD" for a local date. */
export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Parses "YYYY-MM-DD" as local midnight. */
export function parseIso(s: string): Date {
  const [y, m, d] = s.split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

/** Month arithmetic that never rolls over: 31 Jan minus a month is 28/29 Feb. */
export function addMonths(d: Date, n: number): Date {
  const target = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  return new Date(target.getFullYear(), target.getMonth(), Math.min(d.getDate(), lastDay));
}

/** The Monday on or before `d`. getDay() is Sunday-based, so Sunday maps to 6. */
export function startOfWeek(d: Date): Date {
  return addDays(d, -((d.getDay() + 6) % 7));
}

export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

export interface Range { from: string; to: string }

export function rangeFor(anchor: Date, mode: PeriodMode): Range {
  if (mode === "week") {
    const start = startOfWeek(anchor);
    return { from: isoDate(start), to: isoDate(addDays(start, 6)) };
  }
  return { from: isoDate(startOfMonth(anchor)), to: isoDate(endOfMonth(anchor)) };
}

/** Steps one whole period, so the anchor always lands inside the next/previous one. */
export function stepPeriod(anchor: Date, mode: PeriodMode, delta: number): Date {
  return mode === "week" ? addDays(anchor, delta * 7) : addMonths(anchor, delta);
}

/** Whole periods between two anchors: 0 = same, 1 = one in the past. */
export function periodsAgo(anchor: Date, mode: PeriodMode, today: Date): number {
  if (mode === "week") {
    const a = startOfWeek(anchor).getTime();
    const b = startOfWeek(today).getTime();
    return Math.round((b - a) / (7 * 24 * 60 * 60 * 1000));
  }
  return (today.getFullYear() - anchor.getFullYear()) * 12 + (today.getMonth() - anchor.getMonth());
}

/** "This week" / "Last week" / "2 weeks ago" / "Next month" … */
export function relativeLabel(ago: number, mode: PeriodMode): string {
  const unit = mode === "week" ? "week" : "month";
  if (ago === 0) return `This ${unit}`;
  if (ago === 1) return `Last ${unit}`;
  if (ago === -1) return `Next ${unit}`;
  if (ago > 1) return `${ago} ${unit}s ago`;
  return `In ${-ago} ${unit}s`;
}

/**
 * "Oct 5–11, 2026", widening only as far as it must:
 * crossing a month gives "Sep 28–Oct 4, 2026", crossing a year
 * "Dec 29, 2025–Jan 4, 2026".
 */
export function weekLabel(from: string, to: string, locale?: string): string {
  const a = parseIso(from);
  const b = parseIso(to);
  const mon = (d: Date) => d.toLocaleDateString(locale, { month: "short" });

  if (a.getFullYear() !== b.getFullYear()) {
    return `${mon(a)} ${a.getDate()}, ${a.getFullYear()}–${mon(b)} ${b.getDate()}, ${b.getFullYear()}`;
  }
  if (a.getMonth() !== b.getMonth()) {
    return `${mon(a)} ${a.getDate()}–${mon(b)} ${b.getDate()}, ${b.getFullYear()}`;
  }
  return `${mon(a)} ${a.getDate()}–${b.getDate()}, ${b.getFullYear()}`;
}

/**
 * The same range without the year ("Sep 28–Oct 4"), for a narrow card where the
 * full label would wrap onto three lines. The period navigation keeps the year
 * in the accessible name, so nothing is lost for a screen reader.
 */
export function weekLabelShort(from: string, to: string, locale?: string): string {
  const a = parseIso(from);
  const b = parseIso(to);
  const mon = (d: Date) => d.toLocaleDateString(locale, { month: "short" });
  return a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()
    ? `${mon(a)} ${a.getDate()}–${b.getDate()}`
    : `${mon(a)} ${a.getDate()}–${mon(b)} ${b.getDate()}`;
}

/** "October 2026" */
export function monthLabel(anchor: Date, locale?: string): string {
  return anchor.toLocaleDateString(locale, { month: "long", year: "numeric" });
}

/** "Oct 2026" — the narrow-card form. */
export function monthLabelShort(anchor: Date, locale?: string): string {
  return anchor.toLocaleDateString(locale, { month: "short", year: "numeric" });
}

/** "Tue, 6 Oct" — the selected-day strip's heading. */
export function dayLabel(date: string, locale?: string): string {
  const d = parseIso(date);
  return `${d.toLocaleDateString(locale, { weekday: "short" })}, ${d.getDate()} ${d.toLocaleDateString(locale, { month: "short" })}`;
}

/** "Tuesday 6 October" — spoken form for aria-labels and tooltips. */
export function dayLongLabel(date: string, locale?: string): string {
  const d = parseIso(date);
  return `${d.toLocaleDateString(locale, { weekday: "long" })} ${d.getDate()} ${d.toLocaleDateString(locale, { month: "long" })}`;
}

/** Monday-first blank cells before the 1st, so the grid lines up under Mon..Sun. */
export function leadingBlanks(anchor: Date): number {
  return (startOfMonth(anchor).getDay() + 6) % 7;
}

/** Mon..Sun headers in the viewer's locale. */
export function weekdayNames(locale?: string): string[] {
  // 2024-01-01 was a Monday, so seven days from it give Mon..Sun in order.
  return Array.from({ length: 7 }, (_, i) =>
    new Date(2024, 0, 1 + i).toLocaleDateString(locale, { weekday: "short" }));
}

/** Only these three are ever drawn as a letter. */
export const LETTER: Partial<Record<DayStatus, string>> = {
  present: "P",
  pending: "?",
  absent:  "A",
  leave:   "L",
};

export const STATUS_TEXT: Record<DayStatus, string> = {
  present:    "Present",
  pending:    "Awaiting approval",
  absent:     "Absent",
  leave:      "Leave",
  holiday:    "Holiday",
  weekly_off: "Weekly off",
  future:     "Upcoming",
  none:       "No record",
};

/**
 * Collapses the API's richer cell statuses onto the three the widget shows.
 * Half-day and an incomplete (checked in, not out) day are all simply
 * Present: this view never exposes times, hours or lateness.
 */
export function toDayStatus(cellStatus: string): DayStatus {
  switch (cellStatus) {
    case "PRESENT":
    case "PRESENT_INCOMPLETE":
    case "HALF_DAY":
      return "present";
    case "PENDING_APPROVAL": return "pending";
    case "LEAVE":     return "leave";
    case "ABSENT":    return "absent";
    case "HOLIDAY":   return "holiday";
    case "OFF_DAY":   return "weekly_off";
    case "FUTURE":    return "future";
    default:          return "none";   // NOT_EMPLOYED, SUMMARY, anything new
  }
}

export interface Totals { present: number; absent: number; leave: number }

/** Counts come from the same day list the boxes render, so they cannot disagree. */
export function totalsFor(days: AttendanceDay[]): Totals {
  return days.reduce<Totals>((t, d) => {
    if (d.status === "present") t.present += 1;
    else if (d.status === "absent") t.absent += 1;
    else if (d.status === "leave") t.leave += 1;
    return t;
  }, { present: 0, absent: 0, leave: 0 });
}
